import * as React from "react"
import { createGraphQLTraceStore } from "@digits-shared/helpers/createGraphQLTraceStore"
import Session from "@digits-shared/session/Session"

const SESSION_EVENTS = [
  Session.CREATE_EVENT_NAMESPACE,
  Session.UPDATE_EVENT_NAMESPACE,
  Session.DELETE_EVENT_NAMESPACE,
  Session.USER_PREFERENCES_CHANGE,
]
const NO_CONTEXT = () => ""

export function useBuildGraphQLTracer(
  session: Session,
  getContextKey: () => string = NO_CONTEXT,
  contextChangeEvent?: string
) {
  const getScope = React.useCallback(
    () =>
      session.hasUserData && session.isDigitsEmployee
        ? JSON.stringify([session.user.id, getContextKey()])
        : "",
    [session, getContextKey]
  )
  const subscribe = React.useCallback(
    (onChange: () => void) => {
      const events = contextChangeEvent ? [...SESSION_EVENTS, contextChangeEvent] : SESSION_EVENTS
      events.forEach((event) => session.on(event, onChange))
      return () => {
        events.forEach((event) => session.off(event, onChange))
      }
    },
    [session, contextChangeEvent]
  )
  const scope = React.useSyncExternalStore(subscribe, getScope)
  const store = React.useMemo(
    () => createGraphQLTraceStore(() => !!scope && getScope() === scope),
    [getScope, scope]
  )

  React.useLayoutEffect(() => {
    store.activate()
    // Invalidate synchronously on session events, before React replaces the Apollo client.
    const unsubscribe = subscribe(() => {
      if (getScope() !== scope) store.invalidate()
    })
    return () => {
      unsubscribe()
      store.invalidate()
    }
  }, [store, subscribe, getScope, scope])

  return store
}
